import React from 'react';
import { Play, Search } from 'lucide-react';

interface FinancialRiskToolbarProps {
  filters: {
    severity: string;
    flagType: string;
    keyword: string;
  };
  onFilterChange: (filters: { severity: string; flagType: string; keyword: string }) => void;
  onRunAnalysis: () => void;
  isRunning: boolean;
  hasFindings: boolean;
}

export const FinancialRiskToolbar: React.FC<FinancialRiskToolbarProps> = ({
  filters,
  onFilterChange,
  onRunAnalysis,
  isRunning,
  hasFindings,
}) => {
  return (
    <div className="flex flex-wrap items-center gap-3" data-testid="financial-risk-toolbar">
      {/* Run Analysis button */}
      <button
        onClick={onRunAnalysis}
        disabled={isRunning}
        className="inline-flex items-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors disabled:bg-primary-300 text-sm font-medium"
        data-testid="run-analysis-button"
      >
        {isRunning ? (
          <>
            <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full mr-2" />
            Running...
          </>
        ) : (
          <>
            <Play className="h-4 w-4 mr-2" />
            Run Analysis
          </>
        )}
      </button>

      {/* Filters — only show when we have findings */}
      {hasFindings && (
        <>
          {/* Severity dropdown */}
          <select
            value={filters.severity}
            onChange={(e) =>
              onFilterChange({ ...filters, severity: e.target.value })
            }
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-primary-500 focus:border-primary-500"
            data-testid="severity-filter"
          >
            <option value="">All Severities</option>
            <option value="critical">Critical</option>
            <option value="warning">Warning</option>
            <option value="info">Info</option>
          </select>

          {/* Flag type dropdown */}
          <select
            value={filters.flagType}
            onChange={(e) =>
              onFilterChange({ ...filters, flagType: e.target.value })
            }
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-primary-500 focus:border-primary-500"
            data-testid="flag-type-filter"
          >
            <option value="">All Flag Types</option>
            <option value="urgency_language">Urgency Language</option>
            <option value="payment_timeline">Payment Timeline</option>
            <option value="penalty_clause">Penalty Clause</option>
            <option value="mlr_requirement">MLR Requirement</option>
            <option value="cost_sharing">Cost Sharing</option>
          </select>

          {/* Keyword search */}
          <div className="relative">
            <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              value={filters.keyword}
              onChange={(e) =>
                onFilterChange({ ...filters, keyword: e.target.value })
              }
              placeholder="Search clauses..."
              className="pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-primary-500 focus:border-primary-500 w-48"
              data-testid="keyword-filter"
            />
          </div>
        </>
      )}
    </div>
  );
};
